import React, { ReactNode } from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';
import { WarningIcon } from '@phosphor-icons/react';
import { RCTag, RCTextWithIcon } from '../../';
import type { CommonInputProps } from '../../types';

// `inputId` is a legacy artifact, needs to be refactored away.
export interface RCFormGroupProps extends Omit<CommonInputProps, 'onChange' | 'id' | 'disabled' | 'name'> {
  /** Pass in your input here, e.g. `RCTextInput`. */
  children: ReactNode;

  /** Passed through to the input tag. */
  inputId: string;

  /** Large variant. Use this for simpler forms. */
  large?: boolean;

  /** Appends an ID to the <label>. Useful for aria associations or other accessibility-related tasks. */
  labelID?: string;

  /** Uses a `<fieldset>` wrapper element, and replaces the standard `<label>` element with a `<legend>`.
   * This pattern can be used to label a group of inputs, like checkboxes.
   * */
  useFieldset?: boolean;

  /** Allows for the label text to be visually hidden, while still providing the proper labeling and context to screen readers.
   * This can be used when a nearby element provides sufficient labelling for sighted users.
   */
  visuallyHiddenLabel?: boolean;
}

function HelpText({ children }: { children: ReactNode }) {
  return (
    <div className="rc-form-group__help-text">
      {children}
    </div>
  );
}

export function RCFormGroupAlertText({ errorText, id = undefined }: { errorText: string, id?: string }) {
  return (
    <div className="rc-form-group__error" id={id}>
      <RCTextWithIcon icon={<WarningIcon />} placement="left">
        {errorText}
      </RCTextWithIcon>
    </div>
  );
}

/**
* Renders the presentational code surrounding a given input, and accepts any input as a child.
**Unless you need to use a bespoke input element**, you should probably use one of our prebuilt input components instead (e.g. `RCTextInput`).
**/

export function RCFormGroup({
  children,
  errorText,
  helpText,
  inputId,
  label,
  labelID = undefined,
  large = false,
  tag = undefined,
  required = false,
  useFieldset = false,
  visuallyHiddenLabel = false,
}: RCFormGroupProps) {
  const classes = classNames(
    'rc-form-group',
    { 'rc-form-group--with-error': !!errorText },
    { 'rc-form-group--large': large },
  );

  const showTag = tag && tag !== 'none';

  function ariaLabel() {
    if (!showTag) {
      return undefined;
    }

    return `${label} (${tag})`;
  }

  const labelClasses = classNames(
    'rc-form-group__label',
    { 'rc-form-group__label--visually-hidden': visuallyHiddenLabel },
  );

  const requiredIndicator = (
    required &&
      <span
        className="rc-form-group__label__required"
        title={I18n.t('cadence.RCFormGroup.required')}
        aria-hidden="true"
      />
  );

  const labelElement = useFieldset ? (
    <legend className={labelClasses}>{label}{requiredIndicator}</legend>
  ) : (
    <label
      htmlFor={inputId}
      className={labelClasses}
      aria-label={ariaLabel()}
      id={labelID}
    >
      {label}
      {requiredIndicator}
    </label>
  );

  const WrapperElement = (useFieldset ? 'fieldset' : 'div') as React.ElementType;

  return (
    <WrapperElement className={classes}>
      <div className="rc-form-group__header">
        {labelElement}
        {showTag && (
          <div className="rc-form-group__tag">
            <RCTag variant={tag === 'recommended' ? 'highlight' : 'default'}>
              {I18n.t(`commons.formGroup.${tag}`)}
            </RCTag>
          </div>
        )}
      </div>
      {large && helpText && (
        <HelpText>{helpText}</HelpText>
      )}
      <div className="rc-form-group__fields">
        {children}
      </div>
      {!large && !!helpText && (
        <HelpText>{helpText}</HelpText>
      )}
      {errorText && <RCFormGroupAlertText errorText={errorText} />}
    </WrapperElement>
  );
}
